import { DEFAULT_WORKDAYS, formatDateOnly, guessScheduleTaskType } from './schedule';
const MONTHS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
];
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_MS = 86_400_000;
/** 这些字是格子里的注记，不是任何一项的名称 */
const CELL_NOTES = /^non-?workday$/i;
/**
 * @param createdAt 文件的生成日期（PDF 元数据），推年份用；没有就拿 `today`
 */
export function parseCalendarSchedule(pages, opts) {
    if (pages.length === 0)
        return null;
    const hint = Date.parse(`${opts.createdAt ?? opts.today}T00:00:00Z`);
    const segs = [];
    const nonWorkCols = new Set();
    let seenNonWorkNote = false;
    let prev = null;
    let inferredYear = null;
    for (const [p, page] of pages.entries()) {
        const layout = readPageLayout(page);
        if (!layout)
            return null;
        const { texts, month, printedYear, cx0, colW, weeks } = layout;
        // ---- 年份
        let year;
        if (printedYear !== null)
            year = printedYear;
        else if (prev)
            year = month < prev.month ? prev.year + 1 : prev.year;
        else {
            const col1 = layout.firstRowDays.indexOf(1);
            const base = new Date(hint).getUTCFullYear();
            const candidates = [-1, 0, 1, 2]
                .map((d) => base + d)
                .filter((y) => new Date(Date.UTC(y, month, 1)).getUTCDay() === col1);
            candidates.sort((a, b) => Math.abs(Date.UTC(a, month, 1) - hint) - Math.abs(Date.UTC(b, month, 1) - hint));
            const best = candidates[0];
            if (best === undefined)
                return null;
            year = best;
            inferredYear = year;
        }
        prev = { year, month };
        const colOf = (x) => Math.max(0, Math.min(6, Math.round((x - cx0) / colW)));
        const dateOf = (row, col) => {
            const d = weeks[row]?.days[col] ?? 1;
            let m = month;
            if (row === 0 && d > 20)
                m -= 1;
            if (row >= weeks.length - 2 && d < 15)
                m += 1;
            return Date.UTC(year, m, d);
        };
        // 周行的纵向范围：本行日期数字往上一点，到下一行数字
        const rowOf = (y) => weeks.findIndex((w, i) => y <= w.y + 5 && y > (weeks[i + 1]?.y ?? -Infinity) + 5);
        // ---- 哪几列是休息日：格子里印着「Non-workday」
        for (const t of texts) {
            if (CELL_NOTES.test(t.s)) {
                seenNonWorkNote = true;
                nonWorkCols.add(colOf(t.x + t.w / 2));
            }
        }
        // ---- 色块：去掉底色，拼回折行的高横条
        const bars = mergeStackedRects(dropBackgrounds(page.rects, weeks, colW));
        for (const r of bars) {
            const row = rowOf((r.y0 + r.y1) / 2);
            if (row < 0)
                continue;
            const col = colOf((r.x0 + r.x1) / 2);
            const label = texts
                .filter((t) => !CELL_NOTES.test(t.s) && t.x >= r.x0 - 1 && t.x < r.x1 && t.y >= r.y0 - 1 && t.y <= r.y1)
                .sort((a, b) => b.y - a.y || a.x - b.x)
                .map((t) => t.s)
                .join(' ')
                .trim();
            segs.push({ page: p + 1, weekRow: row + 1, lane: Math.round(r.y1), col, date: dateOf(row, col), color: r.fill, label });
        }
    }
    const lanes = new Map();
    for (const s of segs) {
        const key = `${s.page}:${s.weekRow}:${s.lane}`;
        const list = lanes.get(key);
        if (list)
            list.push(s);
        else
            lanes.set(key, [s]);
    }
    const runs = [];
    for (const list of lanes.values()) {
        list.sort((a, b) => a.col - b.col);
        let cur = null;
        for (const s of list) {
            if (cur && s.col === cur.lastCol + 1 && !s.label) {
                cur.end = s.date;
                cur.lastCol = s.col;
                continue;
            }
            cur = { label: s.label, start: s.date, end: s.date, lastCol: s.col, color: s.color, refs: [`${s.page}-${s.weekRow}`] };
            runs.push(cur);
        }
    }
    // ---- 跨周、跨页：同名且首尾相接（或重叠 —— 相邻两页重复印的那一周）就合并
    runs.sort((a, b) => a.start - b.start);
    const merged = [];
    for (const r of runs) {
        if (!r.label)
            continue; // 没名字的孤块：多半是装饰，交给人补
        const hit = merged.find((m) => m.label === r.label && r.start - m.end <= DAY_MS && r.end >= m.start - DAY_MS);
        if (hit) {
            hit.start = Math.min(hit.start, r.start);
            hit.end = Math.max(hit.end, r.end);
            for (const ref of r.refs)
                if (!hit.refs.includes(ref))
                    hit.refs.push(ref);
        }
        else
            merged.push({ ...r, refs: [...r.refs] });
    }
    const workdays = seenNonWorkNote
        ? [0, 1, 2, 3, 4, 5, 6].filter((d) => !nonWorkCols.has(d))
        : [...DEFAULT_WORKDAYS];
    return {
        format: 'calendar-month',
        workdays: workdays.length > 0 ? workdays : [...DEFAULT_WORKDAYS],
        inferredYear,
        tasks: merged.map((r) => ({
            name: r.label,
            type: guessScheduleTaskType(r.label),
            plannedStart: formatDateOnly(r.start),
            plannedFinish: formatDateOnly(r.end),
            color: r.color,
            sourceRef: r.refs.map((ref) => {
                const [page = 0, weekRow = 0] = ref.split('-').map(Number);
                return { page, weekRow };
            }),
        })),
    };
}
function readPageLayout(page) {
    // 加粗的字会被画两遍（同一位置同一串），先去重
    const seen = new Set();
    const texts = page.texts
        .map((t) => ({ ...t, s: t.s.trim() }))
        .filter((t) => {
        if (!t.s)
            return false;
        const key = `${t.s}|${t.x.toFixed(0)}|${t.y.toFixed(0)}`;
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
    // 月份标题：「September」或「September 2026」
    let month = -1;
    let printedYear = null;
    let titleY = -Infinity;
    for (const t of texts) {
        const m = /^([A-Za-z]+)(?:\s+(\d{4}))?$/.exec(t.s);
        const idx = m?.[1] ? MONTHS.indexOf(m[1].toLowerCase()) : -1;
        if (idx >= 0 && t.y > titleY) {
            month = idx;
            printedYear = m?.[2] ? Number(m[2]) : null;
            titleY = t.y;
        }
    }
    if (month < 0)
        return null;
    // 星期表头：七个都要找得到，而且在同一行
    const heads = WEEKDAYS.map((d) => texts.find((t) => t.s.toLowerCase() === d || t.s.toLowerCase() === d.slice(0, 3)));
    if (heads.some((h) => !h))
        return null;
    const sun = heads[0];
    const sat = heads[6];
    if (!sun || !sat || heads.some((h) => !h || Math.abs(h.y - sun.y) > 2))
        return null;
    const headY = sun.y;
    const cx0 = sun.x + sun.w / 2;
    const colW = (sat.x + sat.w / 2 - cx0) / 6;
    if (!(colW > 0))
        return null;
    // 周行：表头下面、同一高度上正好 7 个 1–31 的数字，从左到右
    const byY = new Map();
    for (const t of texts) {
        if (!/^\d{1,2}$/.test(t.s) || t.y >= headY)
            continue;
        const key = t.y.toFixed(1);
        const list = byY.get(key);
        if (list)
            list.push(t);
        else
            byY.set(key, [t]);
    }
    const weeks = [...byY.entries()]
        .filter(([, v]) => v.length === 7)
        .map(([y, v]) => ({ y: Number(y), days: v.sort((a, b) => a.x - b.x).map((t) => Number(t.s)) }))
        .filter((w) => w.days.every((d) => d >= 1 && d <= 31))
        .sort((a, b) => b.y - a.y);
    const firstRowDays = weeks[0]?.days;
    if (weeks.length < 4 || !firstRowDays || !firstRowDays.includes(1))
        return null;
    return { texts, month, printedYear, cx0, colW, weeks, firstRowDays };
}
/**
 * 去掉格子的底色，只留横条。
 *
 * 底色的特征：**它垫在日期数字底下**（周末格子的浅蓝底从日期那一行就开始了），横条从来不盖住日期数字。
 * 按颜色出现在哪几列去猜不可靠 —— 横跨整个月的一项，周末那段也是浅一号的颜色、也每个周末都有。
 */
function dropBackgrounds(rects, weeks, colW) {
    const shaped = rects.filter((r) => r.y1 - r.y0 >= 10 && r.x1 - r.x0 > colW * 0.8 && r.x1 - r.x0 < colW * 1.2);
    const background = new Set(['#ffffff']);
    for (const r of shaped) {
        // 日期数字的基线往上一点是字的中间
        if (weeks.some((w) => r.y0 <= w.y + 2 && r.y1 >= w.y + 2))
            background.add(r.fill.toLowerCase());
    }
    return shaped.filter((r) => !background.has(r.fill.toLowerCase()));
}
/** 名称折成两行时，那一格画的是上下两块同色的矩形 —— 拼回一块 */
function mergeStackedRects(rects) {
    const sorted = [...rects].sort((a, b) => b.y1 - a.y1);
    const out = [];
    for (const r of sorted) {
        const above = out.find((m) => m.fill === r.fill && Math.abs(m.x0 - r.x0) < 1 && Math.abs(m.x1 - r.x1) < 1 && Math.abs(m.y0 - r.y1) < 1.5);
        if (above)
            above.y0 = r.y0;
        else
            out.push({ ...r });
    }
    return out;
}
//# sourceMappingURL=schedule-parse.js.map